import { ReactNode } from "react";
import { Link } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import { useUserRoles, AppRole } from "@/hooks/useUserRoles";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ShieldAlert } from "lucide-react";

/**
 * Wraps a route so only signed-in users holding one of `allow` roles can view it.
 * Signed-in users without the role get a clear access-denied screen instead of a
 * silent redirect, so staff know to sign in with the right account.
 */
export default function RoleRoute({
  allow,
  children,
  loginPath = "/auth",
}: {
  allow: AppRole[];
  children: ReactNode;
  loginPath?: string;
}) {
  return (
    <ProtectedRoute>
      <RoleGate allow={allow} loginPath={loginPath}>
        {children}
      </RoleGate>
    </ProtectedRoute>
  );
}

function RoleGate({
  allow,
  children,
  loginPath,
}: {
  allow: AppRole[];
  children: ReactNode;
  loginPath: string;
}) {
  const { roles, isLoading } = useUserRoles();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!roles.some((r) => allow.includes(r))) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-6 text-center space-y-3">
          <span className="mx-auto h-12 w-12 rounded-full bg-destructive/10 text-destructive grid place-items-center">
            <ShieldAlert className="h-6 w-6" />
          </span>
          <h1 className="text-lg font-semibold">Access restricted</h1>
          <p className="text-sm text-muted-foreground">
            This screen is limited to {allow.join(" / ")} accounts. Sign in with a
            permitted account or ask an admin to grant you the role.
          </p>
          <div className="flex justify-center gap-2 pt-1">
            <Button asChild variant="outline"><Link to="/app">Back to dashboard</Link></Button>
            <Button asChild><Link to={loginPath}>Switch account</Link></Button>
          </div>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
